"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { cn } from "@/lib/utils/utils";
interface TeamStoryProps {
  name: string;
  longBio: string;
}
const wordCount = (s: string) => s.split(/\s+/).filter(Boolean).length;

// Re-paginate the whole bio into pages of roughly equal word count so each page
// takes about the same time to "write" and the height stays steady.
const buildPages = (longBio: string, target = 100): string[] => {
  const text = longBio.replace(/\s+/g, " ").trim();
  const sentences = text.match(/[^.!?]+[.!?]+(?=\s|$)/g)?.map(s => s.trim()) ?? [text];
  const total = sentences.reduce((sum, s) => sum + wordCount(s), 0);
  const pageCount = Math.max(1, Math.round(total / target));
  const per = total / pageCount;
  const pages: string[][] = Array.from({
    length: pageCount
  }, () => []);
  let cumulative = 0;
  let p = 0;
  for (const sentence of sentences) {
    pages[p].push(sentence);
    cumulative += wordCount(sentence);
    if (p < pageCount - 1 && cumulative >= per * (p + 1)) p++;
  }
  return pages.map(chunk => chunk.join(" ")).filter(Boolean);
};
const TeamStory = t0 => {
  const $ = _c(82);
  const {
    name,
    longBio
  } = t0;
  const first = name.split(" ")[0];
  let t1;
  if ($[0] !== longBio) {
    t1 = buildPages(longBio);
    $[0] = longBio;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pages = t1;
  const count = pages.length;
  const reduce = useReducedMotion();
  const [page, setPage] = useState(0);
  const full = pages[page];
  const [typed, setTyped] = useState(pages[0]);
  const firstRun = useRef(true);
  let t2;
  if ($[2] !== full || $[3] !== reduce) {
    t2 = () => {
      if (reduce) {
        return;
      }
      if (firstRun.current) {
        firstRun.current = false;
        return;
      }
      const len = full.length;
      const duration = Math.min(1500, Math.max(700, len * 3.2));
      const start = performance.now();
      let raf = 0;
      const tick = now => {
        const p = Math.min(1, (now - start) / duration);
        setTyped(full.slice(0, Math.floor(p * len)));
        if (p < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          setTyped(full);
        }
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    };
    $[2] = full;
    $[3] = reduce;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== full || $[6] !== page || $[7] !== reduce) {
    t3 = [page, full, reduce];
    $[5] = full;
    $[6] = page;
    $[7] = reduce;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== count || $[10] !== page) {
    t4 = i => {
      if (i < 0 || i >= count || i === page) {
        return;
      }
      setPage(i);
    };
    $[9] = count;
    $[10] = page;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const goto = t4;
  const t5 = "bg-background py-14 md:py-20";
  const T0 = Container;
  const t6 = "px-4 lg:px-10";
  const t7 = "space-y-3";
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SectionSubHeading subtitle="The Long Story" className="text-primary" />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const T1 = SectionTitle;
  const t9 = "WHO ";
  const t10 = first.toUpperCase();
  let t11;
  if ($[13] !== t10) {
    t11 = <>{t9}{t10} REALLY IS.</>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== T1 || $[16] !== t11) {
    t12 = <T1 title={t11} />;
    $[15] = T1;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t8) {
    t13 = <div className={t7}>{t8}{t12}</div>;
    $[18] = t12;
    $[19] = t8;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span aria-hidden={true} className="absolute inset-y-8 left-0 hidden w-1 rounded-full bg-primary/40 md:block" />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = String(page + 1);
  let t16;
  if ($[22] !== t15) {
    t16 = t15.padStart(2, "0");
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t16) {
    t17 = <p className="font-black leading-none tracking-tighter text-7xl text-primary md:text-8xl">{t16}</p>;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const t18 = String(count);
  let t19;
  if ($[26] !== t18) {
    t19 = t18.padStart(2, "0");
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t19) {
    t20 = <p className="mt-2 text-sm font-bold tracking-[0.25em] text-muted-foreground">OF {t19}</p>;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t17 || $[31] !== t20) {
    t21 = <div>{t17}{t20}</div>;
    $[30] = t17;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== goto || $[34] !== page || $[35] !== pages) {
    let t23;
    if ($[37] !== goto || $[38] !== page) {
      t23 = (_, i_0) => <button key={i_0} type="button" onClick={() => goto(i_0)} aria-label={`Page ${i_0 + 1}`} aria-current={i_0 === page} className={cn("h-2 rounded-full transition-all duration-300", i_0 === page ? "w-6 bg-primary" : "w-2 bg-black/20 hover:bg-black/40")} />;
      $[37] = goto;
      $[38] = page;
      $[39] = t23;
    } else {
      t23 = $[39];
    }
    t22 = pages.map(t23);
    $[33] = goto;
    $[34] = page;
    $[35] = pages;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[40] !== t22) {
    t23 = <div className="flex flex-wrap items-center gap-2">{t22}</div>;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== goto || $[43] !== page) {
    t24 = () => goto(page - 1);
    $[42] = goto;
    $[43] = page;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const t25 = page === 0;
  let t26;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ChevronLeft className="h-5 w-5" />;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t24 || $[47] !== t25) {
    t27 = <button type="button" onClick={t24} disabled={t25} aria-label="Previous page" className="grid h-11 w-11 place-items-center rounded-full border-2 border-black bg-white text-black transition-all duration-300 enabled:hover:-translate-y-0.5 enabled:hover:bg-primary enabled:hover:shadow-[3px_3px_0_0_rgba(0,0,0,1)] disabled:cursor-not-allowed disabled:opacity-30">{t26}</button>;
    $[46] = t24;
    $[47] = t25;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== goto || $[50] !== page) {
    t28 = () => goto(page + 1);
    $[49] = goto;
    $[50] = page;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  const t29 = page === count - 1;
  let t30;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ChevronRight className="h-5 w-5" />;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t28 || $[54] !== t29) {
    t31 = <button type="button" onClick={t28} disabled={t29} aria-label="Next page" className="grid h-11 w-11 place-items-center rounded-full border-2 border-black bg-white text-black transition-all duration-300 enabled:hover:-translate-y-0.5 enabled:hover:bg-primary enabled:hover:shadow-[3px_3px_0_0_rgba(0,0,0,1)] disabled:cursor-not-allowed disabled:opacity-30">{t30}</button>;
    $[53] = t28;
    $[54] = t29;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== t27 || $[57] !== t31) {
    t32 = <div className="flex items-center gap-3">{t27}{t31}</div>;
    $[56] = t27;
    $[57] = t31;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== t23 || $[60] !== t32) {
    t33 = <div className="flex flex-col items-start gap-6">{t23}{t32}</div>;
    $[59] = t23;
    $[60] = t32;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== t21 || $[63] !== t33) {
    t34 = <div className="order-2 flex flex-row items-end justify-between gap-6 md:order-1 md:flex-col md:items-start md:justify-between">{t21}{t33}</div>;
    $[62] = t21;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== full) {
    t35 = <p className="text-lg leading-8 text-transparent select-none md:text-xl md:leading-9">{full}</p>;
    $[65] = full;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  const t36 = reduce ? full : typed;
  let t37;
  if ($[67] !== reduce) {
    t37 = !reduce && <span className="ml-1 inline-block h-5 w-0.75 translate-y-0.5 animate-pulse rounded-full bg-primary align-middle md:h-6" />;
    $[67] = reduce;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== t36 || $[70] !== t37) {
    t38 = <p aria-hidden={true} className="absolute inset-0 text-lg leading-8 text-muted-foreground md:text-xl md:leading-9">{t36}{t37}</p>;
    $[69] = t36;
    $[70] = t37;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] !== t35 || $[73] !== t38) {
    t39 = <div className="relative order-1 md:order-2">{t35}{t38}</div>;
    $[72] = t35;
    $[73] = t38;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== t34 || $[76] !== t39) {
    t40 = <div className="mt-10 md:mt-14"><div className="relative w-full overflow-hidden rounded-3xl border-2 border-black bg-secondary p-8 shadow-[8px_8px_0_0_rgba(0,0,0,1)] md:p-14">{t14}<div className="grid gap-10 md:grid-cols-[16rem_1fr] md:gap-14">{t34}{t39}</div></div></div>;
    $[75] = t34;
    $[76] = t39;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== T0 || $[79] !== t13 || $[80] !== t40) {
    t41 = <section className={t5}><T0 className={t6}>{t13}{t40}</T0></section>;
    $[78] = T0;
    $[79] = t13;
    $[80] = t40;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  return t41;
};
export default TeamStory;